import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from './authStore';
import { tryRestoreSession } from '../api/client';
import FlikLogo from '../components/Logo/FlikLogo';

/**
 * Route guard ("auth middleware").
 *
 * Wrap any private route with this component. If an access token is present the
 * route renders immediately. If it isn't, we don't assume the user is logged
 * out: the access token may have expired or been cleared while the httpOnly
 * refresh cookie is still valid, so we attempt a silent refresh first (only when
 * "Mantener sesión" was chosen). Only if that fails do we redirect to
 * `/authenticate?tab=login`, passing the originally requested location along.
 *
 * Presence of the token is re-evaluated on every render (not cached in state):
 * React reuses this component instance across route changes, so a cached
 * decision would let a route render after the session was lost mid-navigation.
 */
function RequireAuth({ children }) {
  const location = useLocation();
  const authed = isAuthenticated();

  // Silent-refresh lifecycle for the "no token" case:
  //   'idle'   → not attempted yet.
  //   'trying' → refresh in flight (show a loader).
  //   'failed' → refresh failed; redirect to login.
  const [refresh, setRefresh] = useState('idle');

  useEffect(() => {
    if (authed) {
      // Token present: make sure a later loss of session can retry.
      if (refresh !== 'idle') setRefresh('idle');
      return undefined;
    }
    if (refresh !== 'idle') return undefined;

    let alive = true;
    setRefresh('trying');
    tryRestoreSession().then((restored) => {
      if (!alive) return;
      // On success the token is now stored, so `authed` flips true on re-render
      // and we fall through to rendering the route.
      setRefresh(restored ? 'idle' : 'failed');
    });
    return () => {
      alive = false;
    };
  }, [authed, refresh]);

  if (authed) {
    return children;
  }

  if (refresh === 'failed') {
    return (
      <Navigate
        to="/authenticate?tab=login"
        replace
        state={{ from: location }}
      />
    );
  }

  // No token yet, silent refresh pending: brief on-brand loader.
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <FlikLogo height={24} />
    </div>
  );
}

export default RequireAuth;
